import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, Subscriber } from 'rxjs';
import { IAppConfig } from '../config/app-config.model';
import { ApiResponse, RegisterRQ } from './api.models';
import { AUTH_KEY, AuthState } from '../auth/auth.models';
import { Router } from '@angular/router';
import { PlatformService } from '../platform.service';
import { AppService } from '../app.service';
import { KeycloakService } from 'keycloak-angular';
import * as uuid from 'uuid';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { UiService } from '../ui.service';
import { LogService } from '../log.service';
import { LanguageService } from '../language.service';
import { XnStorageService } from '../xn-storage';
import { AuthService as OktaService } from '@auth0/auth0-angular';
import { APP_CONFIG } from '../config/app-config.service';

export interface TokenRS extends ApiResponse {
  expiresInSeconds: number;
  grantedPermissions?: string;
  profileId: string;
  requireChangePw: boolean;
  userName: string;
  recordStatus?: string;
  errorsCode?: string;
  firstName?: string
  lastName?: string;
  locale?: string,
}

export interface ApiUserVerificationCodeRQ {
  logonName: string;
  verificationType: string;
}

export interface ApiUserResetPasswordRequestRQ {
  resetPasswordCode: string;
  newPassword: string;
}

export const initialState: AuthState = {
  isAuthenticated: false,
  expiredIn: 0,
  expiredAt: new Date(),
  profileId: '',
  userName: '',
  grantedClaims: [],
  requireChangePw: false,
  recordStatus: '',
  errorsCode: ''
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseApiUrl = 'WBEAuthentication';
  private baseAuthApiUrl = 'WBEAuthentication';
  private profileSubject = new Subject<string>();

  private pingTimer: any = null;

  constructor(
    protected httpClient: HttpClient,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private router: Router,
    private platform: PlatformService,
    private appService: AppService,
    private keycloak: KeycloakService,
    private messageService: MessageService,
    private translate: TranslateService,
    private ui: UiService,
    private log: LogService,
    private language: LanguageService,
    private localStorageService: XnStorageService,
    private oktaService: OktaService,
  ) {

  }

  post<T>(path: string, body?: any, token?: string): Observable<T> {
    let headers = new HttpHeaders();
    let isAuthenticated: boolean = false;

    isAuthenticated = this.isLoggedIn();

    headers = headers.append('verify-session', `${isAuthenticated}`);
    headers = headers.append('ClientID', this.config.apiServer.clientId);

    headers = headers.append('Application', this.config.application);

    headers = headers.append('Content-Type', 'application/json; charset=utf-8');

    if (this.config.isUsingExternalIdentityServer && this.idpToken) {
      headers = headers.append(`Authorization`, `Bearer ${this.idpToken}`);
    }

    return this.httpClient.post<T>(path, body, { headers: headers, withCredentials: true });
  }


  login(userName: string, password: string, token?: string): Observable<AuthState> {
    return new Observable((observable) => {
      const body = {
        username: userName,
        password: password,
        isAdminUser: false
      };
      this.post<TokenRS>(this.baseAuthApiUrl + `/token`, body, token).subscribe({
        next: (tokenRes) => {
          this.handleApiLoginSuccess(observable, tokenRes);
        },
        error: (err) => {
          observable.error(err);
        }
      });
    })
  }

  private loginSuccess(auth: AuthState) {
    this._shown409Error = false;
    this.saveAuthToStorage(auth);
    this.setProfileId(auth.profileId);

    // start timer
    this.startPingTimer();
  }

  pingIntervalTimeInSeconds = 5;
  startPingTimer() {
    this.stopPingTimer();

    this.pingTimer = setInterval(this.handlePingTimer, this.pingIntervalTimeInSeconds * 1000);
  }

  _lastLoginStatus = false;
  handlePingTimer = () => {
    const isLoggedIn = this.isLoggedIn();
  }

  stopPingTimer() {
    if (this.pingTimer != null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  saveAuthToStorage(auth: AuthState) {
    this.localStorageService.setItem(AUTH_KEY, auth);
  }

  refreshToken(): Observable<TokenRS> {
    return this.post(this.baseApiUrl + '/refresh-token');
  }

  autoRefreshToken() {
    this.refreshToken().subscribe(tokenRes => {
      const now = new Date();
      const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);

      const auth = this.getAuth();
      if (!auth) {
        // TODO: handle user is not login
        return;
      }
      const newAuth: AuthState = {
        ...auth,
        expiredIn: tokenRes.expiresInSeconds,
        expiredAt: expiredAt,
      }

      this.saveAuthToStorage(newAuth);
    })
  }

  revokeToken(): Observable<ApiResponse> {
    return this.post(this.baseApiUrl + '/revoke-token');
  }

  userResetPassword(request: ApiUserVerificationCodeRQ): Observable<ApiResponse> {
    return this.post('ResidentPortalUser/ResetPassword', request);
  }

  VerifyResetPasswordCode(request: ApiUserResetPasswordRequestRQ): Observable<ApiResponse> {
    return this.post('ResidentPortalUser/VerifyResetPasswordCode', request);
  }

  autoLogin(token?: string): Observable<TokenRS> {
    return this.post(this.baseApiUrl + '/wbe-auto-login', null, token);
  }

  authAutoLogin(queryString?: string, token?: string): Observable<AuthState | undefined | null> {
    return new Observable((observable) => {
      this.autoLogin(token).subscribe({
        next: (tokenRes) => {
          console.log(queryString)

          if (tokenRes.success) {
            let arrPermissions: string[] = [];

            if (tokenRes.grantedPermissions) {
              arrPermissions = tokenRes.grantedPermissions.split(';');
            }

            const now = new Date();
            const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);

            const auth: AuthState = {
              isAuthenticated: tokenRes.requireChangePw ? false : true,
              expiredIn: tokenRes.expiresInSeconds,
              expiredAt: expiredAt,
              profileId: tokenRes.profileId,
              userName: tokenRes.userName,
              grantedClaims: arrPermissions,
              requireChangePw: tokenRes.requireChangePw,
              queryString: queryString,
              recordStatus: tokenRes.recordStatus,
              firstName: tokenRes.firstName,
              lastName: tokenRes.lastName,
              // userLogonID : tokenRes.userLogonID
            };

            this.loginSuccess(auth);

            if (auth.queryString === 'edit-profile') {
              this.router.navigateByUrl(this.platform.url(`profile/${auth.profileId}/profile-management`));
            }
            else {
              this.router.navigateByUrl(this.platform.url(`dashboard?redirect=1`));
            }

            observable.next(auth);
          } else {
            // auto login failed
            observable.next(null);
          }
        },
        error: (err) => {
          observable.next(err);
        }
      })
    })
  }

  getAuth(): AuthState | undefined {
    if (!this.isLoggedIn()) return undefined;
    return this._getAuth();
  }

  private _getAuth(): AuthState {
    const auth: AuthState = this.localStorageService.getItem(AUTH_KEY);
    return auth;
  }

  getProfileIdValue(): string | null {
    if (!this.isLoggedIn()) return null;
    const auth = this._getAuth();
    return auth ? auth.profileId : null;
  }

  // getUserLogonId(){
  //   if(!this.isLoggedIn()) return null;
  //   const auth = this._getAuth();
  //   return auth ? auth.userLogonID : null;
  // }

  showSessionExpiredMessage() {
    this.ui.toastErrorT('common.error.session-expired', { duration: 5000 });
  }

  handleAuthSessionExpired() {
    // clear data
    this.forceLogout();

    this.showSessionExpiredMessage();

    setTimeout(() => {
      if (this.config.isUsingExternalIdentityServer) {
        // force logout from keycloak
        this.keycloak.logout();
      }
      else {
        this.router.navigateByUrl('/');
      }
    }, 2000);
  }

  userLoggedOut() {
    this._lastLoginStatus = false;
    this.stopPingTimer();

    setTimeout(() => {
      if (this.config.isUsingExternalIdentityServer) {
        // force logout from EIS
        this.forceLogoutFromIDP();
      }
      else {
        this.profileSubject.next("");
      }
    }, 4000);

  }

  isValidAuthData(auth: AuthState) {
    if (!auth || !auth.profileId) return false;

    // check if auth is expired
    if (auth.expiredAt) {
      const expiredAt = new Date(auth.expiredAt as any);
      const now = new Date();
      if (expiredAt < now) {
        return false;
      }
    }

    return true;
  }

  isLoggedIn(): boolean {
    const auth = this._getAuth();

    if (!auth || !auth.profileId) {
      if (this._lastLoginStatus) {
        this.userLoggedOut();
      }
      return false;
    }

    // check if auth is expired
    if (auth.expiredAt) {
      const expiredAt = new Date(auth.expiredAt as any);
      const now = new Date();
      if (expiredAt < now) {
        this.handleAuthSessionExpired();
        return false;
      }
    }

    this._lastLoginStatus = true;
    return true;
  }

  getTokenError() {
    this.showMessage('common.error.forced-logout', 'error');
    this.logout();
  }

  getProfileId(): Observable<string> {
    return this.profileSubject.asObservable();
  }

  onAuthChanged(): Observable<string> {
    return this.getProfileId();
  }

  forceLogout() {
    this.clearData();

    if (this.isUsingExternalIdentityServer()) {
      this.forceLogoutFromIDP();
    }
  }

  clearData() {
    // Retain the previously selected language.
    const currentLang = this.language.getCurrentLanguageCode();
    const currentUtm = this.localStorageService.getItem('UTM-SOURCE-DATA');
    localStorage.clear();

    this.localStorageService.setItem(AUTH_KEY, initialState);
    currentUtm && this.localStorageService.setItem('UTM-SOURCE-DATA', currentUtm);
    sessionStorage.clear();
    this.setProfileId("");
    this.stopPingTimer();
    this._lastLoginStatus = false;
    this.language.saveToLocal(currentLang);
  }

  private _isLoggingOut = false;
  logout(redirect?: boolean, callback?: (status: boolean) => void, delay = 0) {
    if (this._isLoggingOut) return;

    this._isLoggingOut = true;
    this.appService.setSetTimeOut(false);

    this.revokeToken().subscribe({
      next: (res) => {
        this._isLoggingOut = false;
        this.clearData();

        if (this.isUsingExternalIdentityServer()) {
          this.forceLogoutFromIDP();
        }
        else {
          if (redirect) {
            if (delay) {
              setTimeout(() => {
                this.appService.setIsLogout(true);
              }, delay);
            }
            else {
              this.appService.setIsLogout(true);
            }
          }

          callback && callback(true);
        }
      },
      error: (err) => {
        this._isLoggingOut = false;
        callback && callback(false);
      }
    })
  }

  relogin(userName: string, password: string): Observable<AuthState> {
    return new Observable((observable) => {
      if (this.isLoggedIn()) {
        this.logout(false, (status) => {
          this.login(userName, password).subscribe({
            next: (auth) => {
              observable.next(auth);
            },
            error: (err) => {
              observable.error(err);
            }
          });
        })
        return;
      }

      this.login(userName, password).subscribe({
        next: observable.next,
        error: observable.error
      });
    })
  }

  setProfileId(profileId: string) {
    this.profileSubject.next(profileId);
  }

  getDisplayName() {
    if (!this.isLoggedIn()) return '';

    const auth = this.getAuth();
    if (!auth) return '';

    if (auth.lastName && auth.firstName) {
      return `${auth.firstName} ${auth.lastName}`;
    }

    return auth.firstName || auth.userName;
  }

  getFirstName() {
    if (!this.isLoggedIn()) return '';

    const auth = this.getAuth();
    if (!auth) return '';

    return auth.firstName || '';
  }

  getLastName() {
    if (!this.isLoggedIn()) return '';

    const auth = this.getAuth();
    if (!auth) return '';

    return auth.lastName || '';
  }

  private _idpToken: string = '';

  set idpToken(value: string) {
    this._idpToken = value;
  }

  get idpToken(): string {
    return this._idpToken;
  }

  captureIDPAuth(params?: { redirectTo?: String }): Observable<boolean> {
    if (this.config.externalAuthorizationProvider === 'Keycloak') {
      return this.captureKeyloakAuth(params);
    }

    if (this.config.externalAuthorizationProvider === 'Okta') {
      return this.captureOktaAuth(params);
    }

    return new Observable((observable) => {
      setTimeout(() => {
        observable.next(false);
      }, 300);
    });
  }

  private captureOktaAuth(params?: { redirectTo?: String }): Observable<boolean> {
    return new Observable((observable) => {
      if (!this.config.isUsingExternalIdentityServer) {
        observable.next(false);
        return;
      }

      this.oktaService.isAuthenticated$.subscribe(isLoggedIn => {
        if (isLoggedIn) {
          // logged in with keycloak, get token
          this.oktaService.getAccessTokenSilently().subscribe(token => {
            if (token) {
              // call our API to get auth
              this.idpToken = token;

              const path = this.localStorageService.getItem('REDIRECT_URL');
              if (path) {
                this.router.navigateByUrl(path);
              }

              this.processLoginWithIDP().subscribe({
                next: (auth) => {
                  this.processLoginWithIDPSuccess(auth, params);
                  observable.next(auth ? true : false)
                },
                error: (err) => {
                  this.log.log(`captureOktaAuth err: ${err?.message}, status: ${err?.status}, errorCode: ${err?.errorCode}`);
                  observable.next(false);
                  if (err && err.status === 401) {
                    setTimeout(() => {
                      this.forceLogoutFromOkta();
                    }, 2000);
                  }
                }
              });
            }
            else {
              observable.next(false);
            }
          })
        }
        else {
          observable.next(false);
        }
      })
    })
  }

  private processLoginWithIDPSuccess(auth: AuthState, params?: { redirectTo?: String }) {
    if (!auth) {
      return;
    }

    let stop = false;
    if (params && params.redirectTo) {

    }
  }

  private captureKeyloakAuth(params?: { redirectTo?: String }): Observable<boolean> {
    return new Observable((observable) => {
      if (!this.config.isUsingExternalIdentityServer) {
        observable.next(false);
        return;
      }

      const isLoggedIn = this.keycloak.isLoggedIn();
      if (isLoggedIn) {
        // logged in with keycloak, get token
        this.keycloak.getToken().then(token => {
          if (token) {
            // call our API to get auth
            this.idpToken = token;
            this.processLoginWithIDP().subscribe({
              next: auth => {
                this.processLoginWithIDPSuccess(auth, params);
                observable.next(auth ? true : false);
              },
              error: (err) => {
                this.log.log(`processLoginWithKeycloak err: ${err?.message}, status: ${err?.status}, errorCode: ${err?.errorCode}`);
                observable.next(false);
                if (err && err.status === 401) {
                  setTimeout(() => {
                    this.forceLogoutFromKeycloak();
                  }, 2000);
                }
              }
            });
          }
        })
          .catch(err => {
            observable.next(false);
          })
      }
    })

  }

  generateUUID(): string {
    return uuid.v4();
  }

  processLoginWithIDP(): Observable<AuthState> {
    return new Observable((observable) => {
      const body = {
        isAdminUser: false,
        username: this.generateUUID(),
        password: this.generateUUID()
      };
      this._isLogging = true;
      this.post<TokenRS>(this.baseAuthApiUrl + `/token`, body).subscribe({
        next: (tokenRes) => {
          this._isLogging = false;
          this.handleApiLoginSuccess(observable, tokenRes);
        },
        error: (err) => {
          this._isLogging = false;
          observable.error(err);
        }
      });
    })
  }

  private handleApiLoginSuccess(observable: Subscriber<AuthState | null | undefined>, tokenRes: TokenRS) {
    this._shown409Error = false;
    if (tokenRes.success) {
      let arrPermissions: string[] = [];

      if (tokenRes.grantedPermissions) {
        arrPermissions = tokenRes.grantedPermissions.split(';');
      }

      const now = new Date();
      const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);

      const authState: AuthState = {
        isAuthenticated: tokenRes.requireChangePw ? false : true,
        expiredIn: tokenRes.expiresInSeconds,
        expiredAt: expiredAt,
        profileId: tokenRes.profileId,
        userName: tokenRes.userName,
        grantedClaims: arrPermissions,
        requireChangePw: tokenRes.requireChangePw,
        firstName: tokenRes.firstName,
        lastName: tokenRes.lastName,
        locale: tokenRes.locale,
      };

      if (tokenRes.locale) {
        this.language.setLanguage(tokenRes.locale, true);
      }

      if (tokenRes.requireChangePw) {
        this.loginSuccess(authState);
        observable.next(authState);
      } else {
        this.loginSuccess(authState);
        observable.next(authState);
      }
    } else {
      if (tokenRes?.errors[0]?.code == "XWAPI030") {
        const authState: AuthState = {
          isAuthenticated: false,
          expiredIn: tokenRes.expiresInSeconds,
          profileId: tokenRes.profileId,
          userName: tokenRes.userName,
          requireChangePw: tokenRes.requireChangePw,
          errorsCode: tokenRes?.errors[0]?.code
        };
        observable.next(authState);
      }
      else {
        observable.next(null);
      }
    }
  }

  private _shown409Error = false;
  shouldShow409Error(): boolean {
    return !this._shown409Error;
  }

  show409Error() {
    if (!this.shouldShow409Error()) return;
    this._shown409Error = true;
    this.showMessage(`api.error.DS0703`, 'error');
  }

  private _shown401Error = false;
  show401Error() {
    if (this._shown401Error) {
      return;
    }
    this._shown401Error = true;
    this.showMessage(`api.error.api-401`, 'error');
    setTimeout(() => {
      this._shown401Error = false;
    }, 4000);
  }

  showMessage(translateKey: string, type: 'error' | 'success' | 'info' = 'error') {
    const summary = type === 'error' ? 'Error' : (type === 'info' ? 'Information' : 'Success');
    this.translate.get(translateKey).subscribe(value => {
      this.messageService.add({ severity: type, summary: summary, detail: value, key: 'toast', life: 5000 });
    })
  }

  loginWithIDP(returnURL?: string) {
    if (this.config.externalAuthorizationProvider === 'Keycloak') {
      this.loginWithKeycloak(returnURL);
      return;
    }

    if (this.config.externalAuthorizationProvider === 'Okta') {
      this.loginWithOkta(returnURL);
      return;
    }
    this.log.log(`Do not support externalAuthorizationProvider: ${this.config.externalAuthorizationProvider}`);
  }

  private loginWithOkta(returnURL?: string) {
    let redirectURL = returnURL || window.location.href;
    if (!redirectURL.includes('lang=')) {
      if (redirectURL.includes('?')) {
        redirectURL += `&lang=` + this.language.getCurrentLanguageCode()
      }
      else {
        redirectURL += `?lang=` + this.language.getCurrentLanguageCode()
      }
    }
    else {
      redirectURL = redirectURL.replace(/lang=[a-z]{2}/i, `lang=${this.language.getCurrentLanguageCode()}`);
    }

    if (redirectURL) {
      const baseEle = document.querySelector('base');
      if (baseEle) {
        const basePath = baseEle.getAttribute('href');
        let path = redirectURL;
        if (basePath && basePath !== '/') {
          path = path.replace(basePath, '');
        }
        path = path.replace(window.location.origin, '');
        this.localStorageService.setItem(`REDIRECT_URL`, path);
      }

    }

    this.oktaService.loginWithRedirect();
  }

  private loginWithKeycloak(returnURL?: string) {
    let redirectURL = returnURL || window.location.href;
    if (!redirectURL.includes('lang=')) {
      if (redirectURL.includes('?')) {
        redirectURL += `&lang=` + this.language.getCurrentLanguageCode()
      }
      else {
        redirectURL += `?lang=` + this.language.getCurrentLanguageCode()
      }
    }
    else {
      redirectURL = redirectURL.replace(/lang=[a-z]{2}/i, `lang=${this.language.getCurrentLanguageCode()}`);
    }

    this.keycloak.login({
      redirectUri: redirectURL
    })
  }

  private forceLogoutFromIDP() {
    if (this.config.externalAuthorizationProvider === 'Keycloak') {
      return this.forceLogoutFromKeycloak();
    }

    if (this.config.externalAuthorizationProvider === 'Okta') {
      return this.forceLogoutFromOkta();
    }
  }

  rootURL(): string {
    let tag = document.querySelector('base');
    if (tag) {
      let url = tag.href;
      if (url !== '' && url !== '/') {
        return url;
      }
    }
    return window.location.origin;
  }

  private forceLogoutFromOkta() {
    this.oktaService.logout({ logoutParams: { returnTo: this.rootURL() } });
  }

  private forceLogoutFromKeycloak() {
    this.keycloak.logout();
  }

  monitor() {
    if (this.isLoggedIn()) {
      this.startPingTimer();
    }

    // listen localstorage event change
    window.removeEventListener('storage', this._onStorageChanged);
    window.addEventListener('storage', this._onStorageChanged);
  }

  sessionTimeout() {
    this.ui.toastErrorT('common.error.forced-logout', { duration: 10000 });
    // let's user read force logout message in 2 seconds before logging out
    setTimeout(() => {
      this.logout(true);
    }, 3000);
  }

  private getAuthStorageKey(): string {
    return `${this.config.storeKey}${AUTH_KEY}`;
  }

  private _onStorageChanged = (event: StorageEvent) => {
    if (!event) return;

    if (event.key === this.getAuthStorageKey()) {
      this.log.log(`on storage auth changed`);
      this.log.log(event);

      if (!(event as any).isAuthenticated) {
        this.ui.toastT('msg.user_logged_out', { duration: 5000, type: 'info' });
      }

      if (event.oldValue && event.newValue) {
        let oldState: AuthState | undefined;
        let newState: AuthState | undefined;
        try {
          oldState = JSON.parse(event.oldValue);
          newState = JSON.parse(event.newValue);
        } catch (error: any) {
          this.log.log(`Failed to parse auth state ${error?.message}`);
        }

        if (oldState && this.isValidAuthData(oldState) && (!newState || !this.isValidAuthData(newState))) {
          this.log.log(`Storage auth changed => user is logged out.`);
          window.location.reload();
        }
        else if (newState && this.isValidAuthData(newState) && (!oldState || !this.isValidAuthData(oldState))) {
          this.log.log(`Storage auth changed => user is logged in.`);
          window.location.reload();
        }
      }
    }
  }

  private _isLogging = false;
  isLogging() {
    return this._isLogging;
  }

  isUsingExternalIdentityServer(): boolean {
    return this.config.isUsingExternalIdentityServer;
  }

  redirectToRSP(redirectTo: string) {
    const path = `redirect-login/${this.generateUUID()}?redirect_to=${window.encodeURIComponent(redirectTo)}`

    window.location.href = `${this.config.residentPortalSiteURL}/${path}`;
  }

  register(body: RegisterRQ): Observable<ApiResponse> {
    return this.post(`BTRResident/Register`, body);
  }
}
